import React, { useState } from 'react';
import { Video, ExternalLink, Clock, User, Heart, ChevronDown, ChevronUp, Package, Users, Pencil, X } from 'lucide-react';
import AIPVideoCard from './AIPVideoCard';
import AIPCcModal from './AIPCcModal';

const tryParse = (val) => {
    if (!val) return null;
    if (typeof val === 'object') return val;
    try { return JSON.parse(val); } catch { return null; }
};

const statusConfig = {
    completed: { dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', label: 'Completed' },
    processing: { dot: 'bg-blue-500 animate-pulse', text: 'text-blue-700', bg: 'bg-blue-50', label: 'Processing' },
    pending: { dot: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50', label: 'Pending' },
    failed: { dot: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50', label: 'Failed' },
};

const ccLabel = (cc) =>
    cc === undefined || cc === 'loading' ? '…'
        : cc === 'error' ? '?'
            : cc.count > 0 ? `✓${cc.count}${cc.highestCommission != null ? ` (${cc.highestCommission}%)` : ''}`
                : '0';

const AIPMinimizedVideoCard = ({ videos, primary, cc, mode = 'storefront', storefrontName, rawStorefrontName, onPlacementNameChange }) => {
    const [showFull, setShowFull] = useState(false);
    const [expandedVideos, setExpandedVideos] = useState(false);
    const [editingPlacement, setEditingPlacement] = useState(false);
    const [tempPlacementName, setTempPlacementName] = useState(storefrontName || '');
    const [showImageModal, setShowImageModal] = useState(false);
    const [showCc, setShowCc] = useState(false);

    const video = primary;
    const videoMeta = tryParse(video.video_meta);
    const commission = tryParse(video.commission_data);
    const product = tryParse(video.product_data);
    const sales = tryParse(video.sales_data);
    const videosData = tryParse(video.videos_data);
    const sc = statusConfig[video.status] || statusConfig.pending;

    const thumbnailUrl = video.image || videoMeta?.image_url;
    const videoCount = videos.length;

    const price = commission?.price != null && !isNaN(Number(commission.price)) ? Number(commission.price) : null;
    const commRate = commission?.commission_rate != null && !isNaN(Number(commission.commission_rate)) ? Number(commission.commission_rate) : null;
    const commPerSale = commission?.commission_per_sale != null && !isNaN(Number(commission.commission_per_sale)) ? Number(commission.commission_per_sale) : null;
    const estSales = sales?.estimated_monthly_sales != null && Number(sales.estimated_monthly_sales) > 0 ? Number(sales.estimated_monthly_sales) : null;
    const estRev = sales?.estimated_monthly_revenue != null && Number(sales.estimated_monthly_revenue) > 0 ? Number(sales.estimated_monthly_revenue) : null;
    const bsr = sales?.sales_rank != null && Number(sales.sales_rank) > 0 ? Number(sales.sales_rank) : null;
    const rankDrops30 = sales?.sales_rank_drops_30 != null && Number(sales.sales_rank_drops_30) > 0 ? Number(sales.sales_rank_drops_30) : null;
    const hearts = videoMeta?.heart_count != null && Number(videoMeta.heart_count) > 0 ? Number(videoMeta.heart_count) : null;
    const totalInfVideos = videosData?.total_inf_videos != null && Number(videosData.total_inf_videos) > 0 ? Number(videosData.total_inf_videos) : null;

    // Carousel stats
    const upperAvg = videosData?.upper_carousel?.avg_videos_per_fetch ?? null;
    const upperAvgInf = videosData?.upper_carousel?.avg_influencer_per_fetch ?? null;
    const upperAvgNonInf = upperAvg != null && upperAvgInf != null ? parseFloat((upperAvg - upperAvgInf).toFixed(1)) : null;
    const bottomAvg = videosData?.bottom_carousel?.avg_videos_per_fetch ?? null;
    const bottomAvgInf = videosData?.bottom_carousel?.avg_influencer_per_fetch ?? null;
    const bottomAvgNonInf = bottomAvg != null && bottomAvgInf != null ? parseFloat((bottomAvg - bottomAvgInf).toFixed(1)) : null;

    // Storefront placement - always uses the raw storefront name from data, not user input
    const getStorefrontPlacement = () => {
        if (!rawStorefrontName || !videosData) return null;
        const name = rawStorefrontName.toLowerCase().trim();
        const normalize = s => s.replace(/[^a-z]/gi, '').toLowerCase();
        const upperCount = (videosData.upper_carousel?.influencer_names || [])
            .filter(n => typeof n === 'string' && normalize(n).includes(normalize(name))).length;
        const bottomCount = (videosData.bottom_carousel?.influencer_names || [])
            .filter(n => typeof n === 'string' && normalize(n).includes(normalize(name))).length;
        const parts = [];
        if (upperCount > 0) parts.push(`TOP(${upperCount})`);
        if (bottomCount > 0) parts.push(`BOTTOM(${bottomCount})`);
        if (parts.length === 0) return { label: 'NONE', color: '#991b1b' };
        if (parts.length === 2) return { label: `BOTH(${upperCount + bottomCount})`, color: '#166534' };
        return { label: parts.join(' '), color: '#92400e' };
    };
    const placement = video.status === 'completed' ? getStorefrontPlacement() : null;
    const userPlacement = video.status === 'completed' ? (() => {
        if (!storefrontName || !videosData) return null;
        const name = storefrontName.toLowerCase().trim();
        const normalize = s => s.replace(/[^a-z]/gi, '').toLowerCase();
        const upperCount = (videosData.upper_carousel?.influencer_names || [])
            .filter(n => typeof n === 'string' && normalize(n).includes(normalize(name))).length;
        const bottomCount = (videosData.bottom_carousel?.influencer_names || [])
            .filter(n => typeof n === 'string' && normalize(n).includes(normalize(name))).length;
        const parts = [];
        if (upperCount > 0) parts.push(`TOP(${upperCount})`);
        if (bottomCount > 0) parts.push(`BOTTOM(${bottomCount})`);
        if (parts.length === 0) return { label: 'NONE', color: '#991b1b' };
        if (parts.length === 2) return { label: `BOTH(${upperCount + bottomCount})`, color: '#166534' };
        return { label: parts.join(' '), color: '#92400e' };
    })() : null;

    const isStorefront = mode === 'storefront';

    // Storefront: video title is primary; Reverse: product title
    const displayTitle = isStorefront
        ? (videoMeta?.video_title || video.title || '')
        : (video.title || videoMeta?.video_title || '');

    // How many other videos share this ASIN (storefront only - siblings minus self)
    const siblingCount = isStorefront ? videoCount - 1 : 0;

    return (
        <>
            <div className="border border-gray-200 rounded-lg bg-white hover:shadow-md transition-all duration-200 p-3">
                {/* Main Row */}
                <div className="flex items-center gap-3">
                    {/* Thumbnail */}
                    <div className="relative flex-shrink-0">
                        {thumbnailUrl ? (
                            <img
                                src={thumbnailUrl}
                                alt=""
                                className="w-14 h-14 object-contain rounded-lg border border-gray-200 bg-white p-0.5 cursor-zoom-in hover:opacity-80 transition"
                                onClick={e => { e.stopPropagation(); setShowImageModal(true); }}
                            />
                        ) : (
                            <div className="w-14 h-14 bg-gradient-to-br from-gray-100 to-gray-50 rounded-lg border border-gray-200 flex items-center justify-center">
                                {isStorefront ? <Video size={20} className="text-gray-300" /> : <Package size={20} className="text-gray-300" />}
                            </div>
                        )}
                        <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${sc.dot} border-2 border-white shadow-sm`} />
                    </div>

                    {/* Title + Meta */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                            {video.asin && (
                                <span className="font-mono text-[11px] font-bold bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                                    {video.asin}
                                </span>
                            )}
                            <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide ${sc.text} ${sc.bg}`}>
                                {sc.label}
                            </span>
                            {isStorefront && placement && (
                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border"
                                    style={{ color: placement.color, borderColor: placement.color, background: placement.color + '15' }}>
                                    {placement.label}
                                </span>
                            )}
                            {isStorefront && siblingCount > 0 && (
                                <span className="text-[9px] font-bold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded-full border border-purple-200">
                                    +{siblingCount} video{siblingCount > 1 ? 's' : ''} on ASIN
                                </span>
                            )}
                            {!isStorefront && product?.category && (
                                <span className="hidden md:inline text-[9px] text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded-full truncate max-w-[120px]">
                                    {product.category}
                                </span>
                            )}
                        </div>
                        <p className="text-xs font-medium text-gray-800 truncate leading-snug">{displayTitle}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                            {isStorefront ? (
                                <>
                                    {videoMeta?.vendor_name && (
                                        <span className="text-[10px] text-gray-500 flex items-center gap-0.5 font-semibold">
                                            <User size={9} /> {videoMeta.vendor_name}
                                        </span>
                                    )}
                                    {videoMeta?.duration && (
                                        <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                                            <Clock size={9} /> {videoMeta.duration}
                                        </span>
                                    )}
                                    {hearts != null && (
                                        <span className="text-[10px] text-pink-500 flex items-center gap-0.5 font-semibold">
                                            <Heart size={9} /> {hearts}
                                        </span>
                                    )}
                                    {video.created_at && (
                                        <span className="hidden lg:block text-[9px] text-black text-center leading-tight">
                                            Found at {new Date(video.created_at).toLocaleDateString()}
                                        </span>
                                    )}
                                </>
                            ) : (
                                <>
                                    {product?.brand && (
                                        <span className="text-[10px] text-gray-500 font-semibold">{product.brand}</span>
                                    )}
                                    {product?.category && (
                                        <span className="text-[10px] text-gray-400 md:hidden">{product.category}</span>
                                    )}
                                    {totalInfVideos != null && (
                                        <span className="text-[10px] text-purple-500 flex items-center gap-0.5 font-semibold">
                                            <Video size={9} /> {totalInfVideos} inf. videos
                                        </span>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Stat Boxes */}
                    <div className={`hidden lg:grid ${isStorefront ? 'grid-cols-8' : 'grid-cols-9'} gap-1.5 flex-shrink-0`}>
                        <MiniStat label="TOP" value={upperAvg != null ? `${upperAvg}=${upperAvgNonInf}+${upperAvgInf}` : '-'} color="purple" />
                        <MiniStat label="BOTTOM" value={bottomAvg != null ? `${bottomAvg}=${bottomAvgNonInf}+${bottomAvgInf}` : '-'} color="purple" />
                        <MiniStat label="Total Inf." value={totalInfVideos != null ? totalInfVideos : '-'} color="sky" />
                        <div className="bg-gray-50 border border-gray-100 rounded px-2 py-1 text-center flex flex-col items-center justify-center">
                            <div className="text-[8px] uppercase font-semibold text-gray-400">My Placement</div>
                            {userPlacement && (
                                <div className="text-[13px] font-bold leading-tight" style={{ color: userPlacement.color }}>{userPlacement.label}</div>
                            )}
                            {editingPlacement ? (
                                <input
                                    type="text"
                                    value={tempPlacementName}
                                    onChange={e => setTempPlacementName(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') { onPlacementNameChange(tempPlacementName); setEditingPlacement(false); }
                                        if (e.key === 'Escape') setEditingPlacement(false);
                                    }}
                                    onBlur={() => { onPlacementNameChange(tempPlacementName); setEditingPlacement(false); }}
                                    autoFocus
                                    className="max-w-[70px] text-[9px] text-center border border-teal-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-teal-500 mt-0.5"
                                    onClick={e => e.stopPropagation()}
                                />
                            ) : (
                                <div className="flex items-center justify-center gap-0.5">
                                    <span className="text-[9px] text-gray-400 truncate max-w-[55px]">{storefrontName || '—'}</span>
                                    {onPlacementNameChange && (
                                        <button onClick={e => { e.stopPropagation(); setTempPlacementName(storefrontName || ''); setEditingPlacement(true); }}
                                            className="p-0.5 flex-shrink-0">
                                            <Pencil size={7} className="text-gray-400" />
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        <MiniStat label="Price" value={price != null ? `$${price.toFixed(2)}` : '-'} color="emerald" />
                        <MiniStat label="Est. Sales" value={estSales != null ? estSales : '-'} color="blue" />
                        {!isStorefront && <MiniStat label="Rev/mo" value={estRev != null ? `$${estRev.toLocaleString()}` : '-'} color="purple" />}
                        <MiniStat label="BSR" value={bsr != null ? `#${bsr.toLocaleString()}` : '-'} color="gray" />
                        <button type="button" disabled={!(cc?.count > 0)}
                            onClick={e => { e.stopPropagation(); setShowCc(true); }}
                            title={cc?.count > 0 ? 'View campaigns' : undefined}
                            className={`grid rounded transition ${cc?.count > 0 ? 'cursor-pointer hover:ring-2 hover:ring-green-300' : 'cursor-default'}`}>
                            <MiniStat label="CC" value={ccLabel(cc)} customColor={cc?.count > 0 ? '#166534' : '#9ca3af'} />
                        </button>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        {video.asin && (
                            <a
                                href={`https://www.amazon.com/dp/${video.asin}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition border border-amber-200"
                                title="View on Amazon"
                                onClick={e => e.stopPropagation()}
                            >
                                <Package size={14} />
                            </a>
                        )}
                        {videoMeta?.vdp_url && (
                            <a
                                href={videoMeta.vdp_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-100 transition border border-purple-200"
                                title="Watch Video"
                                onClick={e => e.stopPropagation()}
                            >
                                <Video size={14} />
                            </a>
                        )}
                        <button
                            onClick={() => setShowFull(true)}
                            className="px-2.5 py-1.5 rounded-lg bg-teal-50 text-teal-700 hover:bg-teal-100 transition border border-teal-200 text-[10px] font-bold uppercase tracking-wide"
                        >
                            Details
                        </button>
                        {isStorefront && siblingCount > 0 && (
                            <button
                                onClick={() => setExpandedVideos(!expandedVideos)}
                                className="p-1.5 rounded-lg bg-purple-50 text-purple-500 hover:bg-purple-100 transition border border-purple-200"
                                title={`${siblingCount} other video${siblingCount > 1 ? 's' : ''} on this ASIN`}
                            >
                                {expandedVideos ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                        )}
                    </div>
                </div>

                {/* Mobile stats */}
                <div className="lg:hidden grid grid-cols-4 gap-1 mt-2">
                    <MiniStat label="TOP" value={upperAvg != null ? `${upperAvg}=${upperAvgNonInf}+${upperAvgInf}` : '-'} color="purple" />
                    <MiniStat label="BOTTOM" value={bottomAvg != null ? `${bottomAvg}=${bottomAvgNonInf}+${bottomAvgInf}` : '-'} color="purple" />
                    <MiniStat label="Total Inf." value={totalInfVideos != null ? totalInfVideos : '-'} color="sky" />
                    <MiniStat label="Price" value={price != null ? `$${price.toFixed(2)}` : '-'} sub={commRate != null ? `${commRate}%` : null} color="emerald" />
                </div>

                {/* Expanded: show sibling videos on same ASIN (storefront only) */}
                {expandedVideos && isStorefront && siblingCount > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                        <div className="text-[10px] uppercase font-bold text-gray-400 mb-1">
                            All {videoCount} videos for {video.asin}
                        </div>
                        {videos.map((v, i) => {
                            const vm = tryParse(v.video_meta);
                            const vsc = statusConfig[v.status] || statusConfig.pending;
                            const isCurrent = v.id === primary.id;
                            return (
                                <div key={v.id} className={`flex items-center gap-2 p-2 rounded-lg ${isCurrent ? 'bg-teal-50 border border-teal-100' : 'bg-gray-50'}`}>
                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${vsc.dot}`} />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs text-gray-700 truncate">{vm?.video_title || v.title || 'Untitled'}</p>
                                        <div className="flex items-center gap-2 text-[10px] text-gray-400">
                                            {vm?.vendor_name && <span>{vm.vendor_name}</span>}
                                            {vm?.duration && <span>· {vm.duration}</span>}
                                            {vm?.heart_count > 0 && <span>· <Heart size={8} className="inline" /> {vm.heart_count}</span>}
                                        </div>
                                    </div>
                                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${vsc.text} ${vsc.bg}`}>
                                        {vsc.label}
                                    </span>
                                    {isCurrent && <span className="text-[8px] text-teal-500 font-bold">CURRENT</span>}
                                    {vm?.vdp_url && (
                                        <a
                                            href={vm.vdp_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-purple-500 hover:text-purple-700 flex-shrink-0"
                                        >
                                            <ExternalLink size={12} />
                                        </a>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Full card modal */}
            {showFull && (
                <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 pt-20 overflow-y-auto" onClick={() => setShowFull(false)}>
                    <div className="w-full max-w-4xl mb-8" onClick={e => e.stopPropagation()}>
                        <AIPVideoCard videos={videos} primary={primary} cc={cc} mode={mode} storefrontName={storefrontName} rawStorefrontName={rawStorefrontName} onPlacementNameChange={onPlacementNameChange} onClose={() => setShowFull(false)} />
                    </div>
                </div>
            )}

            {showCc && (
                <AIPCcModal asin={video.asin} title={displayTitle} image={thumbnailUrl} cc={cc} onClose={() => setShowCc(false)} />
            )}

            {/* Image zoom modal */}
            {showImageModal && thumbnailUrl && (
                <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowImageModal(false)}>
                    <div className="relative max-w-2xl max-h-[90vh]" onClick={e => e.stopPropagation()}>
                        <img src={thumbnailUrl} alt="" className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl" />
                        <button
                            onClick={() => setShowImageModal(false)}
                            className="absolute -top-3 -right-3 bg-white rounded-full p-1.5 shadow-lg hover:bg-gray-100 transition"
                        >
                            <X size={16} className="text-gray-700" />
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

const colorMap = {
    gray: 'text-gray-700',
    emerald: 'text-emerald-700',
    blue: 'text-blue-700',
    purple: 'text-purple-700',
    sky: 'text-sky-700',
    pink: 'text-pink-700',
};

const MiniStat = ({ label, value, sub, color = 'gray', customColor }) => (
    <div className="bg-gray-50 border border-gray-100 rounded px-2 py-1 text-center flex flex-col items-center justify-center">
        <div className="text-[8px] uppercase font-semibold text-gray-400">{label}</div>
        <div className={`text-[11px] font-bold ${customColor ? '' : (colorMap[color] || colorMap.gray)}`} style={customColor ? { color: customColor } : undefined}>{value}</div>
        {sub && <div className="text-[8px] text-gray-400">{sub}</div>}
    </div>
);

export default AIPMinimizedVideoCard;
